<template>
  <section class="strategy-infograph">
    <h2 class="strategy-infograph__title">{{ title }}</h2>
    <p class="strategy-infograph__sub-title">{{ subTitle }}</p>

    <div class="strategy-infograph__graph">
      <TvaBackgroundVideo
        class="strategy-infograph__graph__video"
        :pathToVideo="`line-animation_${smallscreen ? 'mobile' : 'desktop'}`"
        autoPlay
      />

      <component
        :is="smallscreen ? StrategySVGMobile : StrategySVG"
        :language="i18n.locale.value"
        class="strategy-infograph__graph__svg"
      >
        <SquareIconButton :icon="plus" @onClick="showStrategyModal(true)" />
      </component>
    </div>

    <div class="strategy-infograph__buttons">
      <StrategyButton
        v-for="option of buttonOptions"
        :key="option"
        :label="option"
        :selected="option === selectedOption"
        @onClick="selectedOption = option"
      />
    </div>

    <!-- <transition name="fade" mode="out-in"> -->
    <div class="strategy-infograph__bottom" :key="selectedOption">
      <template v-if="smallscreen">
        <FlickitySlider :key="selectedOption + i18n.locale.value" graph>
          <StrategyItem
            v-for="item of selectedItems"
            :key="item.iconName + item.value"
            :iconName="item.iconName"
            :value="item.value"
            :unit="item.unit"
            :description="item.description"
            class="strategy-infograph__bottom__item"
          />
        </FlickitySlider>
      </template>

      <template v-else>
        <StrategyItem
          v-for="item of selectedItems"
          :key="item.iconName + item.value"
          :iconName="item.iconName"
          :value="item.value"
          :unit="item.unit"
          :description="item.description"
          class="strategy-infograph__bottom__item"
        />
      </template>
    </div>
    <!-- </transition> -->

    <div class="strategy-infograph__note">
      {{ note }}
    </div>

    <StrategyModal
      v-bind="modalContent"
      :show="modalShown"
      @onClose="showStrategyModal(false)"
    />
  </section>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import useTvaMq from "@/plugins/tvaMq.js";
import { useI18n } from "vue-i18n";

import StrategySVG from "./StrategySVG.vue";
import StrategySVGMobile from "./StrategySVGMobile.vue";
import StrategyButton from "./StrategyButton.vue";
import StrategyItem from "./StrategyItem.vue";
import StrategyModal from "./StrategyModal.vue";

import TvaBackgroundVideo from "../../general/TvaBackgroundVideo.vue";
import SquareIconButton from "../../general/SquareIconButton.vue";

import { plus } from "@/assets/icons/icons.js";
import FlickitySlider from "../../general/FlickitySlider.vue";

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  note: String,
  title: String,
  subTitle: String,
  modalContent: Object,
});

const { $tvaMq } = useTvaMq();
const i18n = useI18n();
const smallscreen = computed(() => ["tablet", "mobile"].includes($tvaMq.value));

const selectedOption = ref("");
const modalShown = ref(false);

const buttonOptions = computed(() => Object.keys(props.options));
const selectedItems = computed(() => props.options[selectedOption.value]);

onMounted(() => {
  selectedOption.value = buttonOptions.value[0];
});

function showStrategyModal(value) {
  modalShown.value = value;
}
</script>

<style lang="scss" scoped>
.strategy-infograph {
  position: relative;
  padding-top: 5vh;

  .desktop &,
  .large & {
    margin-left: calc(var(--page-padding-h) * -0.5);
  }

  &__title {
    .desktop &,
    .large & {
      @include title-l;

      text-align: center;
    }

    .mobile &,
    .tablet & {
      @include title-s;
    }

    background: linear-gradient(180deg, #27569e 0%, #5384c2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
    margin: toRem(22) 1.25rem 0.25rem;
  }

  &__sub-title {
    @include dida-s;

    margin: 0 1.25rem toRem(22);

    .desktop &,
    .large & {
      text-align: center;
    }
  }

  &__graph {
    --padding-for-video: 5%;

    width: fit-content;
    max-width: 100%;
    margin: 0 auto;
    position: relative;
    padding-bottom: var(--padding-for-video);

    .mobile &,
    .tablet & {
      padding-left: var(--page-padding-h);
      padding-right: var(--page-padding-h);
    }

    &__video {
      height: 50%;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, calc(var(--padding-for-video) * 2));

      .desktop &,
      .large & {
        width: toRem(600);
      }

      .mobile &,
      .tablet & {
        width: 100%;
      }

      ::v-deep(video) {
        width: 100%;
      }
    }

    &__svg {
      position: relative;
      z-index: 1;
      max-width: 100%;
    }
  }

  &__buttons {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: center;
    gap: 0.15rem;
  }

  &__bottom {
    position: relative;
    border-top: 1px solid var(--scala-grigi-gray-3);
    border-bottom: 1px solid var(--scala-grigi-gray-3);
    display: flex;
    justify-content: space-between;
    gap: 1.5rem;
    margin-top: -1rem;

    .desktop &,
    .large & {
      height: toRem(240);
      max-width: 100%;
      overflow: auto;
      padding: toRem(28) calc(var(--page-padding-h) - 3rem) toRem(72)
        calc(var(--page-padding-h) - 3rem);
    }

    .mobile &,
    .tablet & {
      overflow: hidden;
      height: toRem(250);
      padding-left: toRem(30);
      padding-bottom: toRem(43);

      ::v-deep(.flickity-slider) {
        align-items: stretch;
      }

      &__item {
        max-width: toRem(110);

        &:not(:first-child) {
          margin-left: toRem(10);
        }
      }
    }
  }

  &__note {
    @include p-xs;

    position: absolute;
    left: calc(var(--page-padding-h) - 3rem);
    bottom: 2rem;

    .mobile &,
    .tablet & {
      left: toRem(30);
      bottom: 1rem;
    }
  }
}
</style>
